import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { acceptInvite, validateInvitationToken, sendLoginLink } from "@/services/auth";
import {
  trackInviteAccepted,
  trackInviteLoginLinkRequested,
  trackLoginSucceeded,
} from "@/analytics/analytics";
import { fetchCurrentUser } from "@/services/api";
import AuthLayout from "@/components/AuthLayout";
import { SITE_URL } from "@/constants/domains";

type Step = "validating" | "confirm" | "accepting" | "error";

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [step, setStep] = useState<Step>("validating");
  const [error, setError] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);

  // Re-login link state (shown on error)
  const [loginEmail, setLoginEmail] = useState("");
  const [linkStep, setLinkStep] = useState<"form" | "sending" | "sent">("form");

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      const currentUser = await fetchCurrentUser();
      if (cancelled) return;
      if (currentUser) {
        navigate("/", { replace: true });
        return;
      }
      if (!token) {
        setError("No invitation token provided");
        setStep("error");
        return;
      }

      try {
        const result = await validateInvitationToken(token);
        if (cancelled) return;
        if (result.alreadyAccepted) {
          setStep("accepting");
          const login = await acceptInvite(token, false);
          if (cancelled) return;
          if (!login.success) throw new Error("Unable to sign in. Please try again.");
          trackLoginSucceeded({ role: "EndUser", type: "magic_link_login" });
          navigate("/", { replace: true });
          return;
        }
        setInviteEmail(result.email);
        setCustomerName(result.customerName);
        setStep("confirm");
      } catch (err: any) {
        setError(
          err?.message || "This link is no longer available. Request a fresh sign-in link below."
        );
        setStep("error");
      }
    }

    void validate();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  async function handleAccept() {
    try {
      setStep("accepting");
      const result = await acceptInvite(token, true);
      if (result.success) {
        if (!result.alreadyAccepted) trackInviteAccepted();
        trackLoginSucceeded({
          role: "EndUser",
          type: result.isFirstLogin ? "magic_link_signup" : "magic_link_login",
        });
        navigate("/", { replace: true });
      } else {
        setError("Failed to accept invitation. Please try again.");
        setStep("error");
      }
    } catch (err: any) {
      setError(
        err?.message || "This link is no longer available. Request a fresh sign-in link below."
      );
      setStep("error");
    }
  }

  async function handleSendLoginLink() {
    if (!loginEmail) return;
    try {
      setLinkStep("sending");
      trackInviteLoginLinkRequested();
      await sendLoginLink(loginEmail);
      setLinkStep("sent");
    } catch {
      setLinkStep("form");
    }
  }

  if (step === "validating" || step === "accepting") {
    return (
      <AuthLayout>
        <Card className="w-full border-0 bg-transparent shadow-none">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">
              {step === "validating" ? "Validating invitation..." : "Signing you in..."}
            </p>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (step === "confirm") {
    return (
      <AuthLayout subtitle="Your recruiting plan is ready.">
        <Card className="w-full border-0 bg-transparent shadow-none">
          <CardHeader>
            <CardDescription>
              {customerName
                ? `${customerName} set up your personalized recruiting plan — school matches, coach outreach, and a step-by-step roadmap built on real commitment data from 2,000+ programs.`
                : "Your personalized recruiting plan is ready — school matches, coach outreach, and a step-by-step roadmap built on real commitment data from 2,000+ programs."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="tos"
                checked={tosAccepted}
                onCheckedChange={checked => setTosAccepted(checked === true)}
              />
              <label htmlFor="tos" className="text-sm leading-tight cursor-pointer">
                I agree to the{" "}
                <a
                  href={`${SITE_URL}/terms-of-service`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary-text hover:text-primary-text/80"
                >
                  Terms of Service
                </a>
              </label>
            </div>
            <Button className="w-full" disabled={!tosAccepted} onClick={handleAccept}>
              See my matches →
            </Button>
            <div className="text-center pt-2">
              <Link to="/">
                <Button variant="link" className="px-0 h-auto text-sm">
                  Back to sign in
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // error step
  return (
    <AuthLayout>
      <Card className="w-full border-0 bg-transparent shadow-none">
        <CardHeader>
          <CardTitle>Unable to sign in</CardTitle>
          <CardDescription>There was a problem with your invitation link</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription>
              {error || "This link is no longer available. Request a fresh sign-in link below.."}
            </AlertDescription>
          </Alert>
          <div className="text-sm text-muted-foreground">
            <p>
              New invitation links work for 14 days. You can use the same link to return anytime
              before it expires.
            </p>
          </div>

          {linkStep === "sent" ? (
            <div className="space-y-3 pt-2">
              <div className="text-sm text-muted-foreground">
                Check your email for a new login link. Click the link to sign in — no password
                needed.
              </div>
              <Button variant="outline" className="w-full" onClick={() => setLinkStep("form")}>
                Resend login link
              </Button>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <div className="text-sm text-muted-foreground">
                Enter your email to get a new login link:
              </div>
              <Input
                type="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSendLoginLink()}
              />
              <Button
                className="w-full"
                disabled={!loginEmail || linkStep === "sending"}
                onClick={handleSendLoginLink}
              >
                {linkStep === "sending" ? "Sending..." : "Send me a login link"}
              </Button>
            </div>
          )}

          <div className="pt-2">
            <Link to="/">
              <Button variant="link" className="w-full px-0 h-auto text-sm">
                Back to sign in
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default AcceptInvite;
